import { apiSupportedModels } from '@modules/aawz-intelligence-v2/api/apiSupportedModels'
import { STORE_PRINCIPAL } from '@modules/aawz-intelligence-v2/api/chat-event-handlers'
import { modeloValidoOuPadrao } from '@modules/aawz-intelligence-v2/utils/modelos-suportados'
import { hubEAgenteActions } from './hub-e-agente'

const LS_SELECTED_MODEL = 'iaCrmSelectedModel'
const LS_THINKING_ENABLED = 'iaCrmThinkingEnabled'

function persistLocalStorage(key, value) {
  try {
    localStorage.setItem(key, value)
  } catch (e) {
    console.warn('[ia-crm] localStorage write ' + key + ' falhou', e)
  }
}

/**
 * ★ [19/09/2026] Reconcilia contra a lista VIVA antes de gravar — a mesma
 * régua de `SET_SUPPORTED_MODELS`, que até aqui só valia no boot.
 *
 * O modelo também é DA CONVERSA: abrir uma antiga restaura o `model` dela
 * (`view-chat.vue::restaurarEstadoDaConversa`), e uma conversa criada com um
 * id que o backend aposentou reenvenenava o store E o `localStorage` — todo
 * turno seguinte saía com ele e voltava `400 "Modelo nao suportado"`, até
 * numa conversa nova. Medido no banco local: 52 conversas guardam
 * `claude-sonnet-4-6`, que saiu de `GET /supported-models`.
 *
 * Lista vazia (rota falhou, ou ainda não chegou) preserva a escolha — quem
 * decide isso é `modeloValidoOuPadrao`, e é o mesmo motivo de lá: descartar
 * por causa de uma queda de rede seria trocar um defeito por outro.
 */
export const setSelectedModel = function (context, model) {
  const escolhido = modeloValidoOuPadrao(model, {
    models: context.state.supportedModels,
  })
  context.commit('SET_SELECTED_MODEL', escolhido)
  persistLocalStorage(LS_SELECTED_MODEL, escolhido)
}

export const setThinkingEnabled = function (context, value) {
  context.commit('SET_THINKING_ENABLED', value)
  persistLocalStorage(LS_THINKING_ENABLED, String(value))
}

// spec-016 T004 (ADR-016-02) — mesma receita de `setSelectedModel`: commit
// puro + persistência em localStorage, os dois em try/catch (o write pode
// lançar tanto quanto o read do `state.js`).
const LS_VOZ_SELECIONADA = 'iaCrmVozSelecionada'

export const setVozSelecionada = function (context, vozId) {
  context.commit('SET_VOZ_SELECIONADA', vozId || '')
  persistLocalStorage(LS_VOZ_SELECIONADA, vozId || '')
}

/**
 * A lista viva chegou: comita e RECONCILIA a escolha salva com ela.
 *
 * ★ O `localStorage` é reescrito quando a mutação descarta o id salvo (Q7-A08,
 * 03/09/2026). Sem isto o valor obsoleto — `claude-sonnet-4-6`, que saiu de
 * `/supported-models` — voltaria a ser lido no próximo carregamento da página,
 * e todo turno seguiria saindo com um modelo que o backend recusa com 400.
 *
 * O IO de `localStorage` mora aqui, e não na mutação, que segue pura. Mesma
 * receita do `feeFixoIa/setSupportedModels`.
 */
export const setSupportedModels = function (context, payload) {
  const escolhaAnterior = context.state.selectedModel
  context.commit('SET_SUPPORTED_MODELS', payload)

  const escolhaAtual = context.state.selectedModel
  if (escolhaAtual === escolhaAnterior) return

  persistLocalStorage(LS_SELECTED_MODEL, escolhaAtual)
  if (escolhaAnterior && process.env.NODE_ENV === 'development') {
    console.info(
      '[ia-crm] modelo "' +
        escolhaAnterior +
        '" não está em /supported-models — trocado por "' +
        escolhaAtual +
        '"'
    )
  }
}

// Passthrough: o nome é contrato do `SendMessage` (`storeModule + '/loadSupportedModels'`).
// A rede vive em `apiSupportedModels.carregarPara`, que persiste por
// `setSupportedModels` — a action nunca faz fetch.
export const loadSupportedModels = function (context, storeModule) {
  if (context.state.supportedModelsLoaded) return
  // ⚠️ [20/09/2026] O namespace vem de QUEM DESPACHOU, e o default é o fixo.
  // Com conversa viva (`iaConversa:<uuid>`) este módulo é um CLONE: fixando
  // `STORE_PRINCIPAL` aqui, a lista era gravada no slice fixo e o clone ficava
  // com `supportedModels: []` para sempre. Consequência medida na tela:
  // `setSelectedModel` reconcilia contra a lista VIVA e, com a lista vazia,
  // preserva a escolha — então o id aposentado que estava no `localStorage`
  // (`claude-sonnet-4-6`) atravessava intacto e o turno morria no backend com
  // "Modelo nao suportado". Vuex não expõe o próprio namespace à action; por
  // isso ele viaja como payload, do MESMO lugar que já monta o nome do
  // dispatch (`send-message.vue`).
  return apiSupportedModels.carregarPara({
    storeModule: storeModule || STORE_PRINCIPAL,
  })
}

export const setConversations = function (context, payload) {
  context.commit('SET_CONVERSATIONS', payload)
}
export const appendConversations = function (context, payload) {
  context.commit('APPEND_CONVERSATIONS', payload)
}
// `updated_at` opcional — sem ele, agora (ISO; `ordenarPorRecencia` lê os dois
// formatos). Quem despacha é o `view-chat` no envio, não o motor: action que
// só existe neste namespace vira `unknown action type` nas outras superfícies
// (§9.1 do CLAUDE.md do módulo).
export const tocarConversa = function (context, payload) {
  if (!payload || !payload.id) return
  context.commit('TOUCH_CONVERSATION', {
    id: payload.id,
    updated_at: payload.updated_at || new Date().toISOString(),
  })
}
export const setConversationsLoading = function (context, payload) {
  context.commit('SET_CONVERSATIONS_LOADING', payload)
}
export const setConversationsLastUpdate = function (context, payload) {
  context.commit('SET_CONVERSATIONS_LAST_UPDATE', payload)
}
export const setConversationsError = function (context, payload) {
  context.commit('SET_CONVERSATIONS_ERROR', payload)
}

// Setters puros de pastas — NUNCA fazem fetch (a rede vive em apiFolders).
export const setFolders = function (context, payload) {
  context.commit('SET_FOLDERS', payload)
}
export const setFoldersLoading = function (context, payload) {
  context.commit('SET_FOLDERS_LOADING', payload)
}
export const upsertFolder = function (context, payload) {
  context.commit('UPSERT_FOLDER', payload)
}
export const removeFolder = function (context, payload) {
  context.commit('REMOVE_FOLDER', payload)
}
export const setConversationFolder = function (context, payload) {
  context.commit('SET_CONVERSATION_FOLDER', payload)
}

export const setMessages = function (context, payload) {
  context.commit('SET_MESSAGES', payload)
}
export const addMessage = function (context, payload) {
  context.commit('ADD_MESSAGE', payload)
}
export const setMessagesLoading = function (context, payload) {
  context.commit('SET_MESSAGES_LOADING', payload)
}
export const setMessagesLastUpdate = function (context, payload) {
  context.commit('SET_MESSAGES_LAST_UPDATE', payload)
}
export const setSelectedConversation = function (context, payload) {
  context.commit('SET_SELECTED_CONVERSATION', payload)
}

// `{ [id do conector]: estado }` — só os estados APURADOS pela releitura
// (`utils/estado-dos-conectores.js`). Setter puro, como o resto do arquivo.
export const atualizarEstadoDosConectores = function (context, payload) {
  context.commit('ATUALIZAR_ESTADO_DOS_CONECTORES', payload)
}

export const setChatLoading = function (context, payload) {
  context.commit('SET_CHAT_LOADING', payload)
}
export const setResponseTokens = function (context, payload) {
  context.commit('SET_RESPONSE_TOKENS', payload)
}
export const addResponseToken = function (context, payload) {
  context.commit('ADD_RESPONSE_TOKEN', payload)
}
export const addResponseTokens = function (context, payload) {
  context.commit('ADD_RESPONSE_TOKENS', payload)
}
export const resetResponseTokens = function (context) {
  context.commit('RESET_RESPONSE_TOKENS')
}

export const setThinkingSteps = function (context, payload) {
  context.commit('SET_THINKING_STEPS', payload)
}
export const addThinkingStep = function (context, payload) {
  context.commit('ADD_THINKING_STEP', payload)
}
export const resetThinkingSteps = function (context) {
  context.commit('RESET_THINKING_STEPS')
}

export const setToolCalls = function (context, payload) {
  context.commit('SET_TOOL_CALLS', payload)
}
export const addToolCall = function (context, payload) {
  context.commit('ADD_TOOL_CALL', payload)
}
export const removeToolCall = function (context, toolName) {
  context.commit('REMOVE_TOOL_CALL', toolName)
}
export const resetToolCalls = function (context) {
  context.commit('RESET_TOOL_CALLS')
}

export const setPendingConfirmation = function (context, payload) {
  context.commit('SET_PENDING_CONFIRMATION', payload)
}
export const resolvePendingConfirmation = function (context, resolved) {
  context.commit('RESOLVE_PENDING_CONFIRMATION', resolved)
}
export const resetPendingConfirmation = function (context) {
  context.commit('RESET_PENDING_CONFIRMATION')
}

export const setPromptSuggestions = function (context, payload) {
  context.commit('SET_PROMPT_SUGGESTIONS', payload)
}
export const setPromptSuggestionsLoading = function (context, payload) {
  context.commit('SET_PROMPT_SUGGESTIONS_LOADING', payload)
}

export const setPersonaMode = function (context, payload) {
  context.commit('SET_PERSONA_MODE', payload)
}

export const setPersona = function (context, payload) {
  context.commit('SET_PERSONA', payload)
}

// Ver o comentário de `agenteAtivoId` no `state.js`: só a superfície de
// navegação escreve aqui.
export const setAgenteAtivo = function (context, payload) {
  context.commit('SET_AGENTE_ATIVO', payload)
}

export const setServicosCitadosStreaming = function (context, payload) {
  context.commit('SET_SERVICOS_CITADOS_STREAMING', payload)
}
export const resetServicosCitadosStreaming = function (context) {
  context.commit('RESET_SERVICOS_CITADOS_STREAMING')
}

export const setPerguntasAoGestorStreaming = function(context, payload) {
  context.commit('SET_PERGUNTAS_AO_GESTOR_STREAMING', payload)
}
export const resetPerguntasAoGestorStreaming = function(context) {
  context.commit('RESET_PERGUNTAS_AO_GESTOR_STREAMING')
}

export const setPainelStreaming = function(context, payload) {
  context.commit('SET_PAINEL_STREAMING', payload)
}
export const resetPainelStreaming = function(context) {
  context.commit('RESET_PAINEL_STREAMING')
}

// Setters puros da sala ao vivo — o fetch (o stream SSE) mora em `apiChat`.
export const aplicarPainelEvento = function(context, evento) {
  context.commit('ADICIONAR_PAINEL_EVENTO', evento)
}
export const resetPainelEventosStreaming = function(context) {
  context.commit('RESET_PAINEL_EVENTOS_STREAMING')
}

export const updateConversationTopic = function(context, payload) {
  context.commit('UPDATE_CONVERSATION_TOPIC', payload)
}

export const updateMessageFeedback = function (context, payload) {
  context.commit('UPDATE_MESSAGE_FEEDBACK', payload)
}

// ---- Curadoria de sugestões da IA (spec-003/005/006) -----------------------

export const setCuradoriaGroups = function (context, payload) {
  context.commit('SET_CURADORIA_GROUPS', payload)
}
export const setCuradoriaSummary = function (context, payload) {
  context.commit('SET_CURADORIA_SUMMARY', payload)
}
export const setCuradoriaLoading = function (context, payload) {
  context.commit('SET_CURADORIA_LOADING', payload)
}
// spec-006 — setter puro (o fetch mora na classe de API)
export const setCuradoriaPorStatus = function ({ commit }, payload) {
  commit('setCuradoriaPorStatus', payload)
}

export const setFaseDoTurno = ({ commit }, payload) => {
  commit('SET_FASE_DO_TURNO', payload)
}

// ★ [review #2364, item D] Setter puro do último `node-status` do turno —
// quem o alimenta é `apiChat._onNodeStatus`.
export const setNoDoTurno = ({ commit }, payload) => {
  commit('SET_NO_DO_TURNO', payload)
}

// ---- Modo Agente (spec-002-chat-agentico-estilo-claude-code, plan.md D13) --

export const setRunAtual = function(context, payload) {
  context.commit('SET_RUN_ATUAL', payload)
}
export const setRunSeq = function(context, seq) {
  context.commit('SET_RUN_SEQ', seq)
}
export const setRunEstado = function(context, payload) {
  context.commit('SET_RUN_ESTADO', payload)
}
export const resetRunAtual = function(context) {
  context.commit('RESET_RUN_ATUAL')
}
export const setRunCustoCreditos = function(context, custo) {
  context.commit('SET_RUN_CUSTO_CREDITOS', custo)
}

export const setReconectando = function(context, payload) {
  context.commit('SET_RECONECTANDO', payload)
}

export const setPlano = function(context, payload) {
  context.commit('SET_PLANO', payload)
}
export const resetPlano = function(context) {
  context.commit('RESET_PLANO')
}

export const upsertProposta = function(context, payload) {
  context.commit('UPSERT_PROPOSTA', payload)
}
export const resolverProposta = function(context, payload) {
  context.commit('RESOLVE_PROPOSTA', payload)
}
export const invalidarProposta = function(context, payload) {
  context.commit('INVALIDAR_PROPOSTA', payload)
}
export const resetPropostasAbertas = function(context) {
  context.commit('RESET_PROPOSTAS_ABERTAS')
}

export const adicionarBuscaStreaming = function(context, payload) {
  context.commit('ADICIONAR_BUSCA_STREAMING', payload)
}
export const resetBuscaStreaming = function(context) {
  context.commit('RESET_BUSCA_STREAMING')
}
export const adicionarCodigoStreaming = function(context, payload) {
  context.commit('ADICIONAR_CODIGO_STREAMING', payload)
}
export const resetCodigoStreaming = function(context) {
  context.commit('RESET_CODIGO_STREAMING')
}
export const adicionarArquivoStreaming = function(context, payload) {
  context.commit('ADICIONAR_ARQUIVO_STREAMING', payload)
}
export const resetArquivoStreaming = function(context) {
  context.commit('RESET_ARQUIVO_STREAMING')
}
// Os quatro canais que existem só enquanto o TURNO do Modo Agente roda. Ficam
// numa action só porque são resetados de três lugares (início do envio, troca
// de conversa e conversa nova) — com a lista repetida em cada um, o dia em que
// nasce o quinto canal ele é esquecido em dois deles. Medido em 17/09/2026: o
// `arquivoStreaming` sobrevivia à troca de conversa e o painel mostrava
// "Entregas 1" de um turno de OUTRA conversa.
export const resetCanaisDoAgente = function(context) {
  context.commit('RESET_BUSCA_STREAMING')
  context.commit('RESET_CODIGO_STREAMING')
  context.commit('RESET_ARQUIVO_STREAMING')
  context.commit('RESET_PLANO')
}

// spec-001 `plataforma-por-chat` — o evento SSE `finish` traz `usage` e
// `custo_brl`, que o `end` não tem. Setter puro, como os demais.
export const setFinishDoTurno = ({ commit }, payload) => {
  commit('SET_FINISH_DO_TURNO', payload)
}

// spec-001 `preparacao-reuniao` — o evento SSE `plano`. Setter puro; o dedupe
// por `versao` mora em `apiChat.js::_onPlano`, antes do dispatch.
export const setPlanoDoTurno = ({ commit }, payload) => {
  commit('SET_PLANO_DO_TURNO', payload)
}

// ── D6 (spec-010, [T028]) + RF-G2 — hub do turno e agentes da mensagem ─────
//
// Setters puros, no slice COMPARTILHADO `hub-e-agente.js`. Vivem lá, e não
// aqui, porque `apiChat.js` os despacha pelo namespace de quem o instanciou
// (`this.storeNamespace + '/setHubDaConversa'`) e o `APIChat` é estendido pelas
// três superfícies de chat — enquanto moraram só neste store, o drawer do
// Especialista Fee-Based e o chat de agente respondiam `[vuex] unknown action
// type` a cada turno, sem badge e sem erro visível.
export const setHubDaConversa = hubEAgenteActions.setHubDaConversa
export const setAgenteDaMensagem = hubEAgenteActions.setAgenteDaMensagem
export const setAgentesDoTurnoAoVivo = hubEAgenteActions.setAgentesDoTurnoAoVivo

/**
 * Define (ou limpa, com `null`) o cliente que dá escopo à conversa.
 * Setter puro — quem busca cliente é a camada de API, nunca a action.
 */
export const setClienteEscopo = ({ commit }, cliente) => {
  commit('SET_CLIENTE_ESCOPO', cliente || null)
}

/**
 * Define (ou limpa, com `null`) o cliente que a IA resolveu DENTRO da conversa
 * (spec-016 T035) — ver comentário do `state.clienteDaConversa`.
 * Setter puro — quem resolve o cliente é o handler `abrir_cliente`.
 */
export const setClienteDaConversa = ({ commit }, payload) => {
  commit('SET_CLIENTE_DA_CONVERSA', payload || null)
}

/**
 * spec-009 [T006] — setter puro do guard do AC-2.3 (ver comentário do
 * `state.clienteFixadoManualmente`). Quem decide QUANDO gravar é quem chama:
 * o seletor de escopo (escolha manual) e `fixarClienteDaConversa` (T007),
 * nunca o handler do evento `cliente-identificado`.
 */
export const setClienteFixadoManualmente = ({ commit }, payload) => {
  commit('SET_CLIENTE_FIXADO_MANUALMENTE', payload || null)
}

/**
 * Insights da lateral. Setters puros — quem vai à rede é `apiInsights`.
 */
export const setInsights = ({ commit }, payload) => {
  commit('SET_INSIGHTS', payload || [])
}
export const setInsightsLoading = ({ commit }, payload) => {
  commit('SET_INSIGHTS_LOADING', payload)
}
export const setInsightsError = ({ commit }, payload) => {
  commit('SET_INSIGHTS_ERROR', payload)
}

/**
 * Sugestões a triar (faixa do composer). Setters puros — quem vai à rede é o
 * `apiSuggestions`, chamado pela view.
 */
export const setSugestoesATriar = ({ commit }, payload) => {
  commit('SET_SUGESTOES_A_TRIAR', payload)
}
export const setSugestoesATriarLoading = ({ commit }, payload) => {
  commit('SET_SUGESTOES_A_TRIAR_LOADING', payload)
}

// Setter puro do vínculo WhatsApp. Quem faz a rede é `apiWhatsappVinculo`
// (`garantir`/`obter`/…), que grava aqui; as telas leem pelos getters.
export const setWhatsappVinculo = ({ commit }, vinculo) => {
  commit('SET_WHATSAPP_VINCULO', vinculo)
}
